import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { saveAs } from "file-saver";
import { BiExport, BiSpreadsheet } from "react-icons/bi";
import { FaInfoCircle, FaTimesCircle } from "react-icons/fa";
import { TailSpin } from "react-loader-spinner";

import {
  getDatePresetRange,
  isValidCustomRange,
  type DatePresetId,
} from "../../lib/adminExport/datePresets";
import {
  buildUserTransactionsExportBasename,
  buildUserTransactionsExportCsv,
} from "./buildUserTransactionsExportCsv";
import {
  fetchUserTransactionsForExport,
  MAX_USER_EXPORT_PAGES,
  USER_EXPORT_PAGE_LIMIT,
} from "./fetchUserTransactionsExport";
import type { UserActivityTransaction } from "./userActivityTransactionUtils";

const PRESET_OPTIONS: ReadonlyArray<readonly [DatePresetId, string]> = [
  ["last3m", "Last 3 months"],
  ["last6m", "Last 6 months"],
  [
    "lastCalendarYear",
    "Last calendar year (Jan 1 – Dec 31, previous year)",
  ],
  ["custom", "Custom range"],
];

function resolveRange(
  preset: DatePresetId,
  customStart: string,
  customEnd: string
): { startDate: string; endDate: string } | null {
  if (preset === "custom") {
    if (!isValidCustomRange(customStart, customEnd)) return null;
    return { startDate: customStart, endDate: customEnd };
  }
  return getDatePresetRange(preset);
}

type UserTransactionsExportModalProps = {
  open: boolean;
  onClose: () => void;
  userId: string;
  firstName: string;
  lastName: string;
};

export function UserTransactionsExportModal({
  open,
  onClose,
  userId,
  firstName,
  lastName,
}: UserTransactionsExportModalProps) {
  const [preset, setPreset] = useState<DatePresetId>("last3m");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cacheRef = useRef<{
    key: string;
    transactions: UserActivityTransaction[];
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    cacheRef.current = null;
  }, [open, userId]);

  useEffect(() => {
    if (preset !== "custom") {
      setCustomStart("");
      setCustomEnd("");
    }
  }, [preset]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const customInvalid = useMemo(() => {
    if (preset !== "custom") return false;
    if (!customStart || !customEnd) return true;
    return !isValidCustomRange(customStart, customEnd);
  }, [preset, customStart, customEnd]);

  const ensureTransactions = useCallback(async () => {
    const range = resolveRange(preset, customStart, customEnd);
    if (!range) {
      throw new Error("Choose a valid date range.");
    }

    const key = `${preset}|${customStart}|${customEnd}|${userId}`;
    if (cacheRef.current?.key === key) {
      return { range, transactions: cacheRef.current.transactions };
    }

    const transactions = await fetchUserTransactionsForExport(userId, range);
    cacheRef.current = { key, transactions };
    return { range, transactions };
  }, [preset, customStart, customEnd, userId]);

  const handleDownloadCsv = useCallback(async () => {
    if (customInvalid) {
      setError("Select both custom dates with end on or after start.");
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const { range, transactions } = await ensureTransactions();
      const csv = buildUserTransactionsExportCsv(transactions, firstName, lastName);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const base = buildUserTransactionsExportBasename(
        firstName,
        lastName,
        range.startDate,
        range.endDate
      );
      saveAs(blob, `${base}.csv`);
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e
          ? String((e as { message: string }).message)
          : "Export failed.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [customInvalid, ensureTransactions, firstName, lastName]);

  if (!open) return null;

  const maxRows = MAX_USER_EXPORT_PAGES * USER_EXPORT_PAGE_LIMIT;
  const displayName = [firstName, lastName].filter(Boolean).join(" ").trim() || "User";

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto bg-black/75 p-4 font-manrope backdrop-blur-[3px]"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="relative my-auto flex max-h-[min(92vh,680px)] w-full max-w-[460px] flex-col overflow-hidden rounded-2xl border border-[#2C363E] bg-[#13181B] shadow-[0_24px_80px_rgba(0,0,0,0.55),0_0_0_1px_rgba(255,255,255,0.03)_inset]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-export-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="h-1 w-full shrink-0 bg-gradient-to-r from-secondary_button via-[#3BA8E8] to-transparent opacity-90"
          aria-hidden
        />

        <header className="shrink-0 border-b border-[#2C363E]/80 bg-gradient-to-br from-[#1a222d] via-[#151b22] to-[#13181B] px-5 pb-4 pt-4 sm:px-6 sm:pt-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-secondary_button/35 bg-secondary_button/15 text-secondary_button shadow-[0_0_24px_rgba(11,132,203,0.18)]"
                aria-hidden
              >
                <BiExport className="text-xl" />
              </div>
              <div className="min-w-0 pt-0.5">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#617889]">
                  Data export
                </p>
                <h2
                  id="user-export-modal-title"
                  className="font-sora text-lg font-bold leading-tight text-white sm:text-xl"
                >
                  Export transactions
                </h2>
                <p className="mt-1 truncate text-[12px] font-medium text-[#869AA9]">
                  {displayName}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-xl p-2 text-[#869AA9] transition hover:bg-[#273037] hover:text-white"
              aria-label="Close"
            >
              <FaTimesCircle className="text-2xl" />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
          <div className="mb-5 flex gap-3 rounded-xl border border-[#2C363E]/90 bg-[#161E25]/60 px-3.5 py-3">
            <FaInfoCircle
              className="mt-0.5 shrink-0 text-lg text-secondary_button/85"
              aria-hidden
            />
            <p className="text-[12px] leading-relaxed text-[#AEBCC6]">
              Transactions are fetched only for your selected period (up to{" "}
              <span className="font-semibold tabular-nums text-[#E7EBEE]">
                {maxRows.toLocaleString()}
              </span>{" "}
              rows). Wide date ranges may take longer.
            </p>
          </div>

          <fieldset className="min-w-0 border-0 p-0">
            <legend className="mb-2.5 text-[11px] font-bold uppercase tracking-wider text-[#617889]">
              Period
            </legend>
            <div
              className="flex flex-col gap-2"
              role="radiogroup"
              aria-label="Export period"
            >
              {PRESET_OPTIONS.map(([id, label]) => {
                const selected = preset === id;
                return (
                  <label
                    key={id}
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-3 transition ${
                      selected
                        ? "border-secondary_button/50 bg-secondary_button/[0.08] shadow-[inset_0_0_0_1px_rgba(11,132,203,0.22)]"
                        : "border-[#2C363E] bg-[#161E25]/50 hover:border-[#3A4852] hover:bg-[#1C2227]/80"
                    }`}
                  >
                    <input
                      type="radio"
                      name="user-export-preset"
                      checked={selected}
                      onChange={() => setPreset(id)}
                      className="mt-1 h-3.5 w-3.5 shrink-0 accent-secondary_button"
                    />
                    <span
                      className={`text-[13px] leading-snug ${
                        selected ? "text-white" : "text-[#D2DCE4]"
                      }`}
                    >
                      {label}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          {preset === "custom" && (
            <div className="mt-4 rounded-xl border border-[#2C363E] bg-gradient-to-b from-[#0D1114] to-[#0a0e11] p-4">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-[#617889]">
                Custom range
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="user-export-start"
                    className="mb-1.5 block text-[11px] font-medium text-[#869AA9]"
                  >
                    From
                  </label>
                  <input
                    id="user-export-start"
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#13181B] px-3 text-[13px] text-[#E7EBEE] outline-none transition focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
                  />
                </div>
                <div>
                  <label
                    htmlFor="user-export-end"
                    className="mb-1.5 block text-[11px] font-medium text-[#869AA9]"
                  >
                    To
                  </label>
                  <input
                    id="user-export-end"
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#13181B] px-3 text-[13px] text-[#E7EBEE] outline-none transition focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
                  />
                </div>
              </div>
              {customInvalid && (
                <p className="mt-3 text-[11px] font-medium text-amber-400/95">
                  Select both dates with end on or after start.
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="mt-4 flex flex-col gap-2 rounded-xl border border-red-500/45 bg-red-500/[0.12] px-3.5 py-3 text-[12px] text-red-100/95">
              <span className="leading-relaxed">{error}</span>
              <button
                type="button"
                className="self-start text-[11px] font-semibold text-red-200/90 underline decoration-red-400/60 underline-offset-2 hover:text-white"
                onClick={() => setError(null)}
              >
                Dismiss
              </button>
            </div>
          )}
        </div>

        <footer className="shrink-0 border-t border-[#2C363E]/80 bg-[#141a1f]/95 px-5 py-4 sm:px-6">
          <button
            type="button"
            disabled={loading || customInvalid}
            onClick={handleDownloadCsv}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-secondary_button text-[13px] font-semibold text-white shadow-[0_8px_24px_rgba(11,132,203,0.35)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none"
          >
            {loading ? (
              <TailSpin color="#fff" height={22} width={22} />
            ) : (
              <BiSpreadsheet className="text-lg opacity-95" aria-hidden />
            )}
            Download CSV
          </button>
          <p className="mt-3 text-center text-[10px] font-medium uppercase tracking-wider text-[#5C6B76]">
            Press Esc to close
          </p>
        </footer>
      </div>
    </div>
  );
}
